import { ChevronLeft, ChevronRight, ExternalLink, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { DocumentFlagIcons } from '@/components/documents/DocumentFlagIcons';
import { DocumentPreviewModal } from '@/components/documents/DocumentPreviewModal';
import { DocumentStatusBadge } from '@/components/documents/DocumentStatusBadge';
import { cn } from '@/lib/utils';
import { getDocumentQueue } from '@/services/documentService';

import type { DocumentFlags, DocumentQueueData, DocumentQueueItem, DocumentStatus } from '@/types/document.types';

const STATUS_OPTIONS: Array<{ value: string; labelKey: string }> = [
  { value: '', labelKey: 'documents.filters.allStatuses' },
  { value: 'uploaded', labelKey: 'documents.statuses.uploaded' },
  { value: 'flagged', labelKey: 'documents.statuses.flagged' },
  { value: 'verified', labelKey: 'documents.statuses.verified' },
  { value: 'rejected', labelKey: 'documents.statuses.rejected' },
];

const CATEGORY_OPTIONS: Array<{ value: string; labelKey: string }> = [
  { value: '', labelKey: 'documents.filters.allCategories' },
  { value: 'identity', labelKey: 'documents.filters.identity' },
  { value: 'work_authorization', labelKey: 'documents.filters.workAuthorization' },
  { value: 'licensing', labelKey: 'documents.filters.licensing' },
  { value: 'certifications', labelKey: 'documents.filters.certifications' },
];

function formatTypeName(type: string): string {
  return type
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function hasActiveFlags(flags?: DocumentFlags): boolean {
  if (!flags) return false;
  return (
    flags.nameMismatch === true || flags.expired === true || flags.expiringSoon === true || flags.illegible === true
  );
}

export function DocumentsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [data, setData] = useState<DocumentQueueData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewDocumentId, setPreviewDocumentId] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [hasFlagsFilter, setHasFlagsFilter] = useState(false);
  const [page, setPage] = useState(1);

  const loadQueue = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await getDocumentQueue({
        status: statusFilter || undefined,
        category: categoryFilter || undefined,
        from: fromDate || undefined,
        to: toDate || undefined,
        page,
        limit: 20,
      });
      setData(result);
    } catch {
      setError(t('documents.errorLoading'));
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, categoryFilter, fromDate, toDate, page, t]);

  useEffect(() => {
    void loadQueue();
  }, [loadQueue]);

  const handleFilterChange = () => {
    setPage(1);
  };

  const handleViewSubmission = (item: DocumentQueueItem) => {
    void navigate(`/submissions/${item.applicationId}`);
  };

  const filteredItems = useMemo(() => {
    if (!data) return [];
    if (!hasFlagsFilter) return data.items;
    return data.items.filter((item) => hasActiveFlags(item.flags));
  }, [data, hasFlagsFilter]);

  return (
    <div className='space-y-6'>
      {/* Filters */}
      <div className='flex flex-wrap items-center gap-4 bg-white rounded-lg shadow px-4 py-3'>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            handleFilterChange();
          }}
          className='rounded-md border border-gray-300 bg-white pl-3 pr-8 py-2 text-sm text-gray-700 focus:border-primary focus:ring-1 focus:ring-primary outline-none'>
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {t(opt.labelKey)}
            </option>
          ))}
        </select>

        <select
          value={categoryFilter}
          onChange={(e) => {
            setCategoryFilter(e.target.value);
            handleFilterChange();
          }}
          className='rounded-md border border-gray-300 bg-white pl-3 pr-8 py-2 text-sm text-gray-700 focus:border-primary focus:ring-1 focus:ring-primary outline-none'>
          {CATEGORY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {t(opt.labelKey)}
            </option>
          ))}
        </select>

        <div className='flex items-center gap-2'>
          <label className='text-sm text-gray-600'>{t('documents.filters.from')}</label>
          <input
            type='date'
            value={fromDate}
            onChange={(e) => {
              setFromDate(e.target.value);
              handleFilterChange();
            }}
            className='rounded-md border border-gray-300 bg-white pl-3 pr-8 py-2 text-sm text-gray-700 focus:border-primary focus:ring-1 focus:ring-primary outline-none'
          />
        </div>

        <div className='flex items-center gap-2'>
          <label className='text-sm text-gray-600'>{t('documents.filters.to')}</label>
          <input
            type='date'
            value={toDate}
            onChange={(e) => {
              setToDate(e.target.value);
              handleFilterChange();
            }}
            className='rounded-md border border-gray-300 bg-white pl-3 pr-8 py-2 text-sm text-gray-700 focus:border-primary focus:ring-1 focus:ring-primary outline-none'
          />
        </div>

        <label className='flex items-center gap-2 text-sm text-gray-700 cursor-pointer'>
          <input
            type='checkbox'
            checked={hasFlagsFilter}
            onChange={(e) => {
              setHasFlagsFilter(e.target.checked);
            }}
            className='rounded border-gray-300 text-blue-600 focus:ring-blue-500'
          />
          {t('documents.filters.hasFlags')}
        </label>
      </div>

      {/* Error */}
      {error ? (
        <div className='bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700'>{error}</div>
      ) : null}

      {/* Loading */}
      {isLoading ? (
        <div className='flex items-center justify-center min-h-[300px]'>
          <Loader2 className='h-8 w-8 animate-spin text-blue-600' />
        </div>
      ) : null}

      {/* Table */}
      {!isLoading && data ? (
        <div className='bg-white rounded-lg shadow overflow-hidden'>
          {filteredItems.length === 0 ? (
            <div className='p-12 text-center text-gray-500'>{t('documents.noDocuments')}</div>
          ) : (
            <>
              <div className='overflow-x-auto'>
                <table className='min-w-full divide-y divide-gray-200'>
                  <thead className='bg-gray-50'>
                    <tr>
                      <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                        {t('documents.columns.applicant')}
                      </th>
                      <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                        {t('documents.columns.type')}
                      </th>
                      <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                        {t('documents.columns.category')}
                      </th>
                      <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                        {t('documents.columns.status')}
                      </th>
                      <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                        {t('documents.columns.flags')}
                      </th>
                      <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                        {t('documents.columns.uploadedAt')}
                      </th>
                      <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                        {t('documents.columns.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className='bg-white divide-y divide-gray-200'>
                    {filteredItems.map((item) => (
                      <tr key={item.documentId} className='hover:bg-gray-50'>
                        <td className='px-6 py-4 whitespace-nowrap'>
                          <div className='text-sm font-medium text-gray-900'>
                            {item.firstName} {item.lastName}
                          </div>
                          <div className='text-xs text-gray-500'>{item.email}</div>
                          <div className='text-xs font-mono text-gray-400'>{item.applicationNumber}</div>
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap'>
                          <div className='text-sm text-gray-900'>{formatTypeName(item.type)}</div>
                          <div className='text-xs text-gray-500 truncate max-w-[200px]'>{item.filename}</div>
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-700'>
                          {formatTypeName(item.category)}
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap'>
                          <DocumentStatusBadge status={item.status as DocumentStatus} />
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap'>
                          <DocumentFlagIcons flags={item.flags} />
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
                          {formatDate(item.uploadedAt)}
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap'>
                          <div className='flex items-center gap-2'>
                            <button
                              type='button'
                              onClick={() => setPreviewDocumentId(item.documentId)}
                              className='inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md bg-gray-50 text-gray-700 hover:bg-gray-100'>
                              <ExternalLink className='h-3.5 w-3.5' />
                              {t('documents.actions.preview')}
                            </button>
                            <button
                              type='button'
                              onClick={() => handleViewSubmission(item)}
                              className='text-sm text-blue-600 hover:text-blue-800 font-medium'>
                              {t('documents.actions.viewSubmission')}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {data.meta.totalPages > 1 ? (
                <div className='flex items-center justify-between px-6 py-3 border-t border-gray-200'>
                  <div className='text-sm text-gray-500'>
                    {t('documents.pagination.showing', {
                      from: (data.meta.page - 1) * data.meta.limit + 1,
                      to: Math.min(data.meta.page * data.meta.limit, data.meta.total),
                      total: data.meta.total,
                    })}
                  </div>
                  <div className='flex items-center gap-2'>
                    <button
                      type='button'
                      disabled={page === 1}
                      onClick={() => setPage((p) => p - 1)}
                      className={cn(
                        'p-1.5 rounded-md',
                        page === 1 ? 'text-gray-300' : 'text-gray-600 hover:bg-gray-100',
                      )}>
                      <ChevronLeft className='h-5 w-5' />
                    </button>
                    <span className='text-sm text-gray-700'>
                      {page} / {data.meta.totalPages}
                    </span>
                    <button
                      type='button'
                      disabled={page === data.meta.totalPages}
                      onClick={() => setPage((p) => p + 1)}
                      className={cn(
                        'p-1.5 rounded-md',
                        page === data.meta.totalPages ? 'text-gray-300' : 'text-gray-600 hover:bg-gray-100',
                      )}>
                      <ChevronRight className='h-5 w-5' />
                    </button>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : null}

      {/* Preview Modal */}
      {previewDocumentId ? (
        <DocumentPreviewModal
          documentId={previewDocumentId}
          isOpen
          onClose={() => setPreviewDocumentId(null)}
          onStatusChange={() => void loadQueue()}
        />
      ) : null}
    </div>
  );
}
